import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { ARROW_DOWN, ARROW_UP, ENTER, ESCAPE, TAB } from '../../constants';
import useKeys from '../../hooks/useKeys/useKeys';
import { callAll } from '../../utils';
import { Field } from '../Field';
import { Context } from './Context';
import { DISPLAY_NAME_INPUT } from './constants';
const Input = forwardRef(({ placeholder, onClick = () => { }, onChange = () => { }, defaultValue, ...rest }, ref) => {
    const { activeOptionIndex, decrementActiveOptionIndex, id, incrementActiveOptionIndex, isOpen, selectActiveOption, selectedValue, setInputValue, setIsOpen } = useContext(Context);
    const inputRef = useRef(null);
    const initialValue = useMemo(() => `${defaultValue}` || '', [defaultValue]);
    const [value, setValue] = useState(initialValue);
    useImperativeHandle(ref, () => inputRef.current);
    useEffect(() => {
        if (defaultValue) {
            setValue(`${defaultValue}`);
        }
    }, [defaultValue]);
    useEffect(() => {
        if (selectedValue) {
            setValue(selectedValue.name); // Ensure this is treated as a function call
        }
    }, [selectedValue]);
    const handleOnClick = useCallback(() => {
        setIsOpen(true);
    }, [setIsOpen]);
    const handleOnChange = useCallback((evt) => {
        setIsOpen(true);
        setValue(evt.target.value);
        setInputValue(evt.target.value);
        if (onChange) {
            onChange(evt);
        }
    }, [setIsOpen, setInputValue, onChange]);
    const handleOnBlur = useCallback(() => {
        if (selectedValue) {
            setValue(selectedValue.name); // Ensure this is treated as a function call
        }
    }, [selectedValue]);
    const handleKeyDown = useCallback((evt) => {
        if (evt.key === TAB || evt.key === ESCAPE) {
            setIsOpen(false);
        }
        else if (evt.key === ENTER) {
            if (isOpen) {
                selectActiveOption();
            }
            else {
                setIsOpen(true);
            }
        }
    }, [isOpen, setIsOpen, selectActiveOption]);
    const handleArrowKeys = useCallback((evt) => {
        evt.preventDefault();
        if (evt.key === ARROW_DOWN) {
            incrementActiveOptionIndex();
        }
        else if (evt.key === ARROW_UP) {
            decrementActiveOptionIndex();
        }
    }, [incrementActiveOptionIndex, decrementActiveOptionIndex]);
    useKeys([TAB, ESCAPE, ENTER], handleKeyDown, inputRef);
    useKeys([ARROW_DOWN, ARROW_UP], handleArrowKeys, inputRef);
    return (_jsx(Field.Input, { ...rest, "aria-activedescendant": activeOptionIndex >= 0 ? `${id}-option-${activeOptionIndex}` : undefined, "aria-autocomplete": "list", "aria-controls": `${id}-options`, "aria-expanded": isOpen, "aria-labelledby": `${id}-label`, autoComplete: "off", "data-testid": `${id}-input`, id: `${id}-input`, onBlur: handleOnBlur, onChange: callAll(onChange, handleOnChange), onClick: callAll(onClick, handleOnClick), placeholder: placeholder, ref: inputRef, role: "combobox", value: value }));
});
Input.displayName = DISPLAY_NAME_INPUT;
export { Input };
//# sourceMappingURL=Input.js.map